import { all, call, fork, put, takeLatest } from '@redux-saga/core/effects';

import * as Actions from './actions';
import * as ActionsType from './constants';
import {
    fetchBasicPostMethodWithToken,
    fetchGetMethod,
    fetchJSON,
    fetchPostMethod,
    fetchJSONWithoutData,
    GetErrorMessageFromStatus,
    readFailedResponse
} from '../../helpers/api';
import { getApiErrorMessage } from '../../helpers/apiErrorMessage';
import { getLoggedInUser } from '../../helpers/authUtils';
import { addNotification } from '../../helpers/notification';
import { apiServiceUrl } from '../../constants';
import { buildVpInviteReadUrl, buildVpShareReadUrl } from '../../helpers/vpShare';

const vpApiServiceUrl = `${apiServiceUrl}/ValueProposition`

export function* loadValuePropositionsList() {
    try {
        const user = getLoggedInUser();
        const response = yield call(fetchGetMethod, `${vpApiServiceUrl}/GetValueProposition`, user.token);
        const body = yield response.data;

        if (response.status === 200 && body?.status === 200 && body.vpList) {
            yield put(Actions.loadValuePropositionsListSuccess(body.vpList));
        } else {
            yield put(Actions.loadValuePropositionsListFailed(
                getApiErrorMessage({ status: body?.status ?? response.status, body }, ActionsType.VP_LIST_LOAD_FAILED_MESSAGE)
            ));
        }
    } catch (error) {
        yield put(Actions.loadValuePropositionsListFailed(ActionsType.VP_LIST_LOAD_FAILED_MESSAGE));
    }
}

const DUPLICATE_FAILED_MESSAGE = "We couldn't duplicate this value proposition.";

function* reportDuplicateFailure(message) {
    addNotification({ title: 'Duplicate failed', message, type: 'danger' });
    yield put(Actions.duplicateValuePropositionFailed(message));
}

export function* duplicateValueProposition({ payload: { vpId, clientId }}) {
    try {
        const user = getLoggedInUser();
        const body = {
            ValuePropositionId: vpId,
            ClientID: clientId,
        };
        const response = yield call(
            fetchPostMethod,
            `${vpApiServiceUrl}/DuplicateValueProposition`,
            user.token,
            body
        );

        if (response.status === 200) {
            yield put(Actions.duplicateValuePropositionSuccess())
        } else {
            const failedBody = yield Promise.resolve(response.data).catch(() => null);
            yield* reportDuplicateFailure(
                getApiErrorMessage({ status: response.status, body: failedBody }, DUPLICATE_FAILED_MESSAGE)
            );
        }
    } catch (error) {
        yield* reportDuplicateFailure(DUPLICATE_FAILED_MESSAGE);
    }
}

export function* fetchValuePropositionById({ payload: { vpId }}) {
    try {
        const user = getLoggedInUser();
        const response = yield call(fetchGetMethod, `${vpApiServiceUrl}/GetValuePropositionById?id=${vpId}`, user.token);

        if (response.status === 200) {
            const { vp, status, body } = yield response.data.then((response) => {
                return (response.status === 200)
                    ? { vp: response.valueProposition }
                    : { status: response.status, body: response };
            }).catch(() => ({ status: 500 }));
            if (vp) {
                yield put(Actions.fetchValuePropositionByIdSuccess(vp));
            } else {
                yield put(Actions.fetchValuePropositionByIdFailed(
                    GetErrorMessageFromStatus({ status: status }),
                    { status: status ?? 500, body: body ?? null }
                ));
            }
        } else {
            const failure = yield call(readFailedResponse, response);
            yield put(Actions.fetchValuePropositionByIdFailed(GetErrorMessageFromStatus(response), failure));
        }
    } catch (error) {
        yield put(Actions.fetchValuePropositionByIdFailed(GetErrorMessageFromStatus(error)));
    }
}

const VP_SAVE_FAILED_FALLBACK = "We couldn't save this value proposition. Please try again.";

function* failVpSave(message) {
    yield put(Actions.saveValuePropositionFailed(message));
    yield call(addNotification, {
        title: 'Value proposition not saved',
        message,
        type: 'danger',
        dismiss: { duration: 8000 },
    });
}

export function* saveValueProposition({ payload: { vp }}) {
    try {
        const user = getLoggedInUser();
        const response = yield call(
            fetchPostMethod,
            `${vpApiServiceUrl}/SaveValueProposition`,
            user.token,
            vp,
            /* withoutResponseBody */ false
        );
        const body = yield Promise.resolve(response.data).catch(() => null);
        if (response.status === 200 && body?.status === 200 && body?.vpId && body?.clientId) {
            yield put(Actions.saveValuePropositionSuccess(body.vpId, body.clientId));
        } else {
            yield* failVpSave(getApiErrorMessage({ status: response.status, body }, VP_SAVE_FAILED_FALLBACK));
        }
    } catch (error) {
        yield* failVpSave(VP_SAVE_FAILED_FALLBACK);
    }
}


function* changeValuePropositionState({ payload: { vpId, isActive }}) {
    try {
        const user = getLoggedInUser();
        const urlSuffix = isActive ? 'ActiveValueProposition' : 'DeactivateValueProposition';
        const body = { Id: vpId };
        const response = yield call(
            fetchPostMethod,
            `${vpApiServiceUrl}/${urlSuffix}`,
            user.token,
            body,
            /* withoutResponseBody */ true
        );

        if (response.status === 200) {
            yield put(Actions.changeValuePropositionStateSuccess());
        } else {
            yield put(Actions.changeValuePropositionStateFailed(GetErrorMessageFromStatus(response)));
        }
    } catch (error) {
        yield put(Actions.changeValuePropositionStateFailed(GetErrorMessageFromStatus(error)));
    }
}

function* deleteValueProposition({ payload: { vpId }}) {
    try {
        const user = getLoggedInUser();
        const body = { Id: vpId };
        const response = yield call(
            fetchPostMethod,
            `${vpApiServiceUrl}/DeleteValueProposition`,
            user.token,
            body,
            /* withoutResponseBody */ true
        );

        if (response.status === 200) {
            yield put(Actions.deleteValuePropositionSuccess());
        } else {
            yield put(Actions.deleteValuePropositionFailed(GetErrorMessageFromStatus(response)));
        }
    } catch (error) {
        yield put(Actions.deleteValuePropositionFailed(GetErrorMessageFromStatus(error)));
    }
}

function* fetchFollowersByValuePropositionId({ payload: { vpId }}) {
    try {
        const user = getLoggedInUser();
        const response = yield call(
            fetchGetMethod,
            `${vpApiServiceUrl}/GetFollowersByValuePropositionId?id=${vpId}`,
            user.token
        );

        if (response.status === 200) {
            const { followers, status } = yield response.data.then((response) => {
                return (response.status === 200)
                    ? { followers: response.followers }
                    : { status: response.status };
            }).catch(() => ({ status: 500 }));
            if (followers) {
                yield put(Actions.fetchFollowersByValuePropositionIdSuccess(followers));
            } else {
                yield put(Actions.fetchFollowersByValuePropositionIdFailed(GetErrorMessageFromStatus({ status: status })));
            }
        } else {
            yield put(Actions.fetchFollowersByValuePropositionIdFailed(GetErrorMessageFromStatus(response)));
        }
    } catch (error) {
        yield put(Actions.fetchFollowersByValuePropositionIdFailed(GetErrorMessageFromStatus(error)));
    }
}

function* fetchAttachmentsByValuePropositionId({ payload: { vpId }}) {
    try {
        const user = getLoggedInUser();
        const response = yield call(
            fetchGetMethod,
            `${vpApiServiceUrl}/GetAttachmentsByValuePropositionId?id=${vpId}`,
            user.token
        );

        if (response.status === 200) {
            const { attachments, status } = yield response.data.then((response) => {
                return (response.status === 200)
                    ? { attachments: response.attachments }
                    : { status: response.status };
            }).catch(() => ({ status: 500 }));
            if (attachments) {
                yield put(Actions.fetchAttachmentsByValuePropositionIdSuccess(attachments));
            } else {
                yield put(Actions.fetchAttachmentsByValuePropositionIdFailed(GetErrorMessageFromStatus({ status: status })));
            }
        } else {
            yield put(Actions.fetchAttachmentsByValuePropositionIdFailed(GetErrorMessageFromStatus(response)));
        }
    } catch (error) {
        yield put(Actions.fetchAttachmentsByValuePropositionIdFailed(GetErrorMessageFromStatus(error)));
    }
}

function* fetchValuePropositionBySharedKey({ payload: { sharedKey, via }}) {
    yield* readSharedValueProposition(buildVpInviteReadUrl(sharedKey, via));
}

export function* fetchValuePropositionByShareKey({ payload: { shareKey, via }}) {
    yield* readSharedValueProposition(buildVpShareReadUrl(shareKey, via));
}

const sharedReadFailed = (status) =>
    Actions.fetchValuePropositionBySharedKeyFailed(GetErrorMessageFromStatus({ status }), status);

// Both shared reads (Invite Link and Share Link) return the same public model.
// A failure keeps its status (null when the request never reached the server), so
// the page can tell "not available" (403/404) from a failure worth retrying (429, 5xx).
function* readSharedValueProposition(url) {
    try {
        const user = getLoggedInUser();
        const response = yield call(fetchGetMethod, url, user?.token);
        const body = yield response.data.catch(() => null);

        if (response.status === 200 && body?.status === 200) {
            yield put(Actions.fetchValuePropositionBySharedKeySuccess(body.valueProposition));
        } else {
            const status = response.status === 200 ? body?.status ?? 500 : response.status;
            yield put(sharedReadFailed(status));
        }
    } catch (error) {
        yield put(sharedReadFailed(null));
    }
}

export function* watchLoadValuePropositionsList(): any {
    yield takeLatest(ActionsType.LOAD_VALUE_PROPOSITIONS_LIST, loadValuePropositionsList);
}

export function* watchDuplicateValueProposition(): any {
    yield takeLatest(ActionsType.DUPLICATE_VALUE_PROPOSITION, duplicateValueProposition);
}

export function* watchFetchValuePropositionById(): any {
    yield takeLatest(ActionsType.FETCH_VALUE_PROPOSITION_BY_ID, fetchValuePropositionById);
}

export function* watchSaveValueProposition(): any {
    yield takeLatest(ActionsType.SAVE_VALUE_PROPOSITION, saveValueProposition);
}

export function* watchChangeValuePropositionState(): any {
    yield takeLatest(ActionsType.CHANGE_VALUE_PROPOSITION_STATE, changeValuePropositionState);
}

export function* watchDeleteValueProposition(): any {
    yield takeLatest(ActionsType.DELETE_VALUE_PROPOSITION, deleteValueProposition);
}

export function* watchFetchFollowersByValuePropositionId(): any {
    yield takeLatest(ActionsType.FETCH_FOLLOWERS_BY_VALUE_PROPOSITION_ID, fetchFollowersByValuePropositionId);
}

export function* watchFetchAttachmentsByValuePropositionId(): any {
    yield takeLatest(ActionsType.FETCH_ATTACHMENTS_BY_VALUE_PROPOSITION_ID, fetchAttachmentsByValuePropositionId);
}

export function* watchFetchValuePropositionBySharedKey(): any {
    yield takeLatest(ActionsType.FETCH_VALUE_PROPOSITION_BY_SHARED_KEY, fetchValuePropositionBySharedKey);
}

export function* watchFetchValuePropositionByShareKey(): any {
    yield takeLatest(ActionsType.FETCH_VALUE_PROPOSITION_BY_SHARE_KEY, fetchValuePropositionByShareKey);
}

function* prepareForDowngrade() {
    try {
        const user = getLoggedInUser();
        const response = yield call(
            fetchPostMethod,
            `${vpApiServiceUrl}/PrepareForDowngrade`,
            user.token,
            {},
            true
        );

        if (response.status === 200) {
            yield put(Actions.prepareForDowngradeSuccess());
        } else {
            yield put(Actions.prepareForDowngradeFailed(GetErrorMessageFromStatus(response)));
        }
    } catch (error) {
        yield put(Actions.prepareForDowngradeFailed(GetErrorMessageFromStatus(error)));
    }
}

function* getUserOwnedVPs() {
    try {
        const user = getLoggedInUser();
        const response = yield call(
            fetchGetMethod,
            `${vpApiServiceUrl}/GetUserOwnedVPs`,
            user.token
        );

        if (response.status === 200) {
            // response.data is a promise, yield it to get the actual response body
            const responseBody = yield response.data;
            console.log('GetUserOwnedVPs response body:', responseBody);
            
            // API returns { status: 200, vPs: [...] } (note: vPs with mixed case)
            // Also check for other possible formats: VPs, vps
            const statusCode = responseBody.Status || responseBody.status;
            const vpsList = responseBody.VPs || responseBody.vPs || responseBody.vps || [];
            
            console.log('GetUserOwnedVPs - Status:', statusCode, 'VPs count:', vpsList?.length || 0);
            
            if (statusCode === 200 && Array.isArray(vpsList) && vpsList.length > 0) {
                console.log('GetUserOwnedVPs success - VPs array length:', vpsList.length);
                console.log('GetUserOwnedVPs - First VP sample:', vpsList[0]);
                yield put(Actions.getUserOwnedVPsSuccess(vpsList));
            } else {
                console.error('GetUserOwnedVPs failed - invalid response:', { 
                    statusCode, 
                    vpsListLength: vpsList?.length,
                    isArray: Array.isArray(vpsList),
                    responseBodyKeys: Object.keys(responseBody || {})
                });
                yield put(Actions.getUserOwnedVPsFailed(GetErrorMessageFromStatus({ status: statusCode || 500 })));
            }
        } else {
            console.error('GetUserOwnedVPs failed - response status:', response.status);
            yield put(Actions.getUserOwnedVPsFailed(GetErrorMessageFromStatus(response)));
        }
    } catch (error) {
        console.error('Error in getUserOwnedVPs saga:', error);
        yield put(Actions.getUserOwnedVPsFailed(GetErrorMessageFromStatus(error)));
    }
}

function* bulkUpdateVPKeepStatus(action) {
    // Safely extract payload with fallback
    const payload = action?.payload || {};
    const vps = payload?.vps;
    
    console.log('[Saga] bulkUpdateVPKeepStatus - SAGA STARTED', { 
        vpsCount: Array.isArray(vps) ? vps.length : 0, 
        vps: Array.isArray(vps) ? vps.slice(0, 3) : null,
        hasPayload: !!payload,
        hasVps: !!vps
    });
    
    try {
        // Validate vps array
        if (!Array.isArray(vps) || vps.length === 0) {
            console.warn('bulkUpdateVPKeepStatus saga - No VPs to update or invalid vps array');
            yield put(Actions.bulkUpdateVPKeepStatusSuccess());
            return;
        }

        const user = getLoggedInUser();
        if (!user || !user.token) {
            console.error('bulkUpdateVPKeepStatus saga - No user or token');
            yield put(Actions.bulkUpdateVPKeepStatusFailed('User not authenticated'));
            return;
        }

        // Safely map VPs with validation
        const vpUpdates = vps
            .filter(vp => vp && (vp.vpId !== undefined || vp.VpId !== undefined))
            .map(vp => ({
                VpId: vp.vpId || vp.VpId,
                IsLimited: vp.isLimited !== undefined ? vp.isLimited : false
            }));

        if (vpUpdates.length === 0) {
            console.warn('bulkUpdateVPKeepStatus saga - No valid VPs after filtering');
            yield put(Actions.bulkUpdateVPKeepStatusSuccess());
            return;
        }

        const body = {
            VPs: vpUpdates
        };
        
        console.log('bulkUpdateVPKeepStatus saga - calling API:', {
            url: `${vpApiServiceUrl}/BulkUpdateVPKeepStatus`,
            bodyCount: body.VPs.length
        });

        // Use fetchBasicPostMethodWithToken which uses fetchJSON
        // fetchJSON returns { status, data } where data is a Promise
        const response = yield call(
            fetchBasicPostMethodWithToken,
            `${vpApiServiceUrl}/BulkUpdateVPKeepStatus`,
            user.token,
            body
        );
        
        // Safely stringify response for logging (handle circular references)
        let responseString = 'Unable to stringify';
        try {
            responseString = JSON.stringify(response);
        } catch (e) {
            responseString = `[Circular or non-serializable: ${e.message}]`;
        }
        
        console.log('bulkUpdateVPKeepStatus saga - API response received:', {
            status: response?.status,
            hasData: !!response?.data,
            responseType: typeof response,
            responseKeys: response && typeof response === 'object' ? Object.keys(response) : null,
            responseString: responseString
        });

        if (!response) {
            console.error('bulkUpdateVPKeepStatus saga - No response received');
            yield put(Actions.bulkUpdateVPKeepStatusFailed('No response from server'));
            return;
        }

        // Check if response has status property
        if (response.status === undefined || response.status === null) {
            console.error('bulkUpdateVPKeepStatus saga - Response missing status property');
            yield put(Actions.bulkUpdateVPKeepStatusFailed('Invalid response from server: missing status'));
            return;
        }

        if (response.status === 200) {
            // Status is 200, so the update was successful
            // Dispatch success immediately - we don't need the response body
            console.log('bulkUpdateVPKeepStatus saga - Status 200, dispatching success action');
            yield put(Actions.bulkUpdateVPKeepStatusSuccess());
            
            // Optionally try to log response body in background (non-blocking)
            if (response.data && typeof response.data.then === 'function') {
                response.data.then((data) => {
                    console.log('bulkUpdateVPKeepStatus saga - Response body (logged async):', data);
                }).catch((error) => {
                    console.warn('bulkUpdateVPKeepStatus saga - Could not parse response body (non-critical):', error);
                });
            }
        } else {
            console.error('bulkUpdateVPKeepStatus saga - Failed with status:', response.status);
            // Try to get error message from response body
            let errorMessage = 'Request failed';
            try {
                const statusMessage = GetErrorMessageFromStatus(response);
                errorMessage = statusMessage || `Request failed with status ${response.status}`;
            } catch (e) {
                console.warn('bulkUpdateVPKeepStatus saga - Error getting status message:', e);
                errorMessage = `Request failed with status ${response.status}`;
            }
            
            if (response.data && typeof response.data.then === 'function') {
                try {
                    const errorBody = yield response.data.then((data) => data).catch(() => null);
                    if (errorBody && typeof errorBody === 'object') {
                        errorMessage = errorBody.Message || errorBody.message || errorMessage;
                    }
                } catch (e) {
                    // If parsing fails, use default error message
                    console.warn('bulkUpdateVPKeepStatus saga - Could not parse error response:', e);
                }
            }
            yield put(Actions.bulkUpdateVPKeepStatusFailed(errorMessage));
        }
    } catch (error) {
        console.error('bulkUpdateVPKeepStatus saga - Exception caught:', error);
        let errorMessage = 'An unexpected error occurred';
        try {
            if (error && typeof error === 'object') {
                errorMessage = error.message || error.toString() || 'An unexpected error occurred';
                // Try GetErrorMessageFromStatus only if error has status property
                if (error.status !== undefined) {
                    const statusMessage = GetErrorMessageFromStatus(error);
                    if (statusMessage) {
                        errorMessage = statusMessage;
                    }
                }
            } else if (error) {
                errorMessage = String(error);
            }
        } catch (e) {
            console.error('bulkUpdateVPKeepStatus saga - Error processing error message:', e);
            errorMessage = 'An unexpected error occurred';
        }
        yield put(Actions.bulkUpdateVPKeepStatusFailed(errorMessage));
    }
}

export function* watchPrepareForDowngrade(): any {
    yield takeLatest(ActionsType.PREPARE_FOR_DOWNGRADE, prepareForDowngrade);
}

export function* watchGetUserOwnedVPs(): any {
    yield takeLatest(ActionsType.GET_USER_OWNED_VPS, getUserOwnedVPs);
}

export function* watchBulkUpdateVPKeepStatus(): any {
    console.log('[Saga Watcher] watchBulkUpdateVPKeepStatus - Watcher registered');
    yield takeLatest(ActionsType.BULK_UPDATE_VP_KEEP_STATUS, bulkUpdateVPKeepStatus);
}

function* valuePropositions(): any {
    yield all([
        fork(watchLoadValuePropositionsList),
        fork(watchDuplicateValueProposition),
        fork(watchFetchValuePropositionById),
        fork(watchSaveValueProposition),
        fork(watchChangeValuePropositionState),
        fork(watchDeleteValueProposition),
        fork(watchFetchFollowersByValuePropositionId),
        fork(watchFetchValuePropositionBySharedKey),
        fork(watchFetchValuePropositionByShareKey),
        fork(watchFetchAttachmentsByValuePropositionId),
        fork(watchPrepareForDowngrade),
        fork(watchGetUserOwnedVPs),
        fork(watchBulkUpdateVPKeepStatus),
    ]);
}

export default valuePropositions;
